import { useState } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Users,
  Bot,
  Phone,
  AlertTriangle,
  CheckCircle,
  PauseCircle,
  PlayCircle,
  Shield,
  Clock,
  PhoneOff,
  UserCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function CommandCenter() {
  const navigate = useNavigate();
  const {
    user,
    commandCenterData,
    activeSessions,
    pendingApprovals,
    alerts,
    globalPause,
    globalResume,
    takeoverCall,
    fetchCommandCenter
  } = useAdminBoard();

  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [takingOver, setTakingOver] = useState(null);

  // IMMEDIATE ACTION - No confirmation modal per user requirement
  const handleGlobalPause = async () => {
    setIsPausing(true);
    try {
      await globalPause("Emergency pause - Admin action");
      toast.success("GLOBAL PAUSE ACTIVATED - All agents stopped");
      fetchCommandCenter();
    } catch (error) {
      toast.error("Failed to activate global pause");
    } finally {
      setIsPausing(false);
    }
  };

  const handleGlobalResume = async () => {
    setIsResuming(true);
    try {
      await globalResume();
      toast.success("All agents resumed");
      fetchCommandCenter();
    } catch (error) {
      toast.error("Failed to resume agents");
    } finally {
      setIsResuming(false);
    }
  };

  // IMMEDIATE TAKEOVER - No confirmation modal
  const handleTakeover = async (callControlId) => {
    setTakingOver(callControlId);
    try {
      await takeoverCall(callControlId);
      toast.success("TAKEOVER SUCCESSFUL - You have control");
      fetchCommandCenter();
    } catch (error) {
      toast.error("Takeover failed");
    } finally {
      setTakingOver(null);
    }
  };

  const isSuperAdmin = user?.role === "gsk_super_admin";
  const hasActivePause = commandCenterData?.agents_paused > 0;

  return (
    <div className="p-4 lg:p-6 space-y-6 bg-slate-50 min-h-full" data-testid="command-center">
      {/* Header with Global Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Command Center</h1>
          <p className="text-slate-500 text-sm mt-1">
            Real-time operations control
          </p>
        </div>

        {/* CRITICAL OVERRIDE CONTROLS - Always visible for Super Admin */}
        {isSuperAdmin && (
          <div className="flex items-center gap-3">
            {hasActivePause ? (
              <Button
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm"
                onClick={handleGlobalResume}
                disabled={isResuming}
                data-testid="global-resume-btn"
              >
                <PlayCircle className="w-5 h-5 mr-2" />
                {isResuming ? "RESUMING..." : "RESUME ALL"}
              </Button>
            ) : (
              <Button
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg border-2 border-red-700"
                onClick={handleGlobalPause}
                disabled={isPausing}
                data-testid="global-pause-btn"
              >
                <PauseCircle className="w-5 h-5 mr-2" />
                {isPausing ? "PAUSING..." : "PAUSE ALL AGENTS"}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {activeSessions.length}
                </p>
                <p className="text-xs text-slate-500 mt-1">Active Sessions</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                <Phone className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-600">
                  {pendingApprovals.length}
                </p>
                <p className="text-xs text-slate-500 mt-1">Pending Approvals</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold text-emerald-600">
                    {commandCenterData?.agents_active || 0}
                  </p>
                  {commandCenterData?.agents_paused > 0 && (
                    <span className="text-sm text-red-600 font-medium">
                      ({commandCenterData.agents_paused} paused)
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">Active Agents</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Bot className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {commandCenterData?.clients_count || 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">Total Clients</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Sessions - 2 columns */}
        <Card className="lg:col-span-2 bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="w-4 h-4 text-cyan-600" />
              Live Sessions
            </CardTitle>
            <CardDescription className="text-xs">
              Real-time call activity - Click TAKE OVER for immediate control
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeSessions.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Phone className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No active sessions</p>
                <p className="text-xs mt-1">Calls will appear here in real-time</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeSessions.map((session) => (
                  <div
                    key={session.session_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                    data-testid={`session-${session.session_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        session.escalation_score > 0.5 ? "bg-red-500 animate-pulse" : "bg-emerald-500"
                      }`} />
                      <div>
                        <p className="font-medium text-sm text-slate-900">
                          Session {session.session_id?.slice(0, 8)}...
                        </p>
                        <p className="text-xs text-slate-500">
                          Turn {session.turn_count} • {session.status}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          session.escalation_score > 0.5
                            ? "text-red-600 border-red-300 bg-red-50"
                            : "text-emerald-600 border-emerald-300 bg-emerald-50"
                        }
                      >
                        Risk: {Math.round(session.escalation_score * 100)}%
                      </Badge>
                      {/* IMMEDIATE TAKEOVER BUTTON - No confirmation */}
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                        onClick={() => handleTakeover(session.call_control_id)}
                        disabled={takingOver === session.call_control_id}
                        data-testid={`takeover-${session.session_id}`}
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        {takingOver === session.call_control_id ? "..." : "TAKE OVER"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Approvals */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="w-4 h-4 text-amber-600" />
              Pending Approvals
            </CardTitle>
            <CardDescription className="text-xs">
              AI actions requiring human decision
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingApprovals.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">All clear</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingApprovals.slice(0, 5).map((approval) => (
                  <div
                    key={approval.id}
                    className={`p-3 rounded-lg border ${
                      approval.risk_level === "critical" || approval.risk_level === "high"
                        ? "bg-red-50 border-red-200"
                        : "bg-amber-50 border-amber-200"
                    }`}
                    data-testid={`approval-${approval.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge
                        className={
                          approval.risk_level === "critical" || approval.risk_level === "high"
                            ? "bg-red-500 text-white"
                            : "bg-amber-500 text-white"
                        }
                      >
                        {approval.risk_level?.toUpperCase()}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {new Date(approval.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 mb-2 line-clamp-2">
                      {approval.action_description}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        onClick={() => navigate("/admin/approvals")}
                      >
                        Review
                      </Button>
                    </div>
                  </div>
                ))}
                {pendingApprovals.length > 5 && (
                  <Button
                    variant="outline"
                    className="w-full text-xs"
                    onClick={() => navigate("/admin/approvals")}
                  >
                    View All ({pendingApprovals.length})
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Alerts */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Recent Alerts
          </CardTitle>
          <CardDescription className="text-xs">
            System events and escalations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No recent alerts</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.slice(0, 8).map((alert) => (
                <div
                  key={alert.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    alert.severity === "critical"
                      ? "bg-red-50 border-red-200"
                      : alert.severity === "warning"
                      ? "bg-amber-50 border-amber-200"
                      : "bg-slate-50 border-slate-200"
                  }`}
                  data-testid={`alert-${alert.id}`}
                >
                  <AlertTriangle
                    className={`w-4 h-4 flex-shrink-0 ${
                      alert.severity === "critical"
                        ? "text-red-600"
                        : alert.severity === "warning"
                        ? "text-amber-600"
                        : "text-slate-500"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{alert.message}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
